/**
 * Works out an enquiry's client team from the person who raised it.
 *
 * The team is looked up from the sender's email when the ticket arrives. When
 * that missed — the sender was not a Backoffice user yet, or the address is a
 * shared mailbox — this lets an analyst repeat the lookup, and offers the one
 * team that has users on the sender's email domain as a suggestion. A
 * suggestion is only ever applied by the analyst clicking it. Hidden once the
 * enquiry has a team.
 */

import { useState } from 'react';

import { Alert, Button, Group, Stack, Text } from '@mantine/core';

import type { EnquiryTeamResolution, EnquiryTeamResolutionReason } from '@/types/enquiry';

interface EnquiryTeamLookupProps {
  requesterEmail: string | null;
  /** The enquiry's team; the lookup is only offered while there is none. */
  teamId: string | null;
  /** Repeat the lookup. Resolves to `undefined` if it could not be run. */
  onLookup: () => Promise<EnquiryTeamResolution | undefined>;
  /** Assign the suggested team. */
  onUseSuggestion: (teamId: string) => Promise<void>;
  /** A team's display name, for the suggestion. */
  teamName: (teamId: string) => string;
}

function domainOf(email: string | null): string {
  return email?.includes('@') ? email.split('@').pop()!.toLowerCase() : '';
}

function explain(reason: EnquiryTeamResolutionReason | null, domain: string): string | null {
  switch (reason) {
    case 'no_requester_email':
      return 'This enquiry has no requester email to look up.';
    case 'internal_sender':
      return "The sender is GTI staff, so a client team can't be worked out from the address.";
    case 'free_mail_domain':
      return "The sender uses a personal mail address, which doesn't identify a company.";
    case 'no_team_on_domain':
      return domain ? `No team has users on @${domain} yet.` : 'No team matches the requester.';
    case 'several_teams_on_domain':
      return `More than one team has users on @${domain}, so no single team is suggested.`;
    default:
      return null;
  }
}

export function EnquiryTeamLookup({
  requesterEmail,
  teamId,
  onLookup,
  onUseSuggestion,
  teamName,
}: Readonly<EnquiryTeamLookupProps>) {
  const [looking, setLooking] = useState(false);
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState<EnquiryTeamResolution | null>(null);

  if (teamId !== null) {
    return null;
  }

  if (!requesterEmail) {
    return (
      <Text size="sm" c="dimmed" data-testid="team-lookup-no-email">
        There is no requester email on this enquiry, so choose the team below.
      </Text>
    );
  }

  const lookUp = async () => {
    setLooking(true);
    try {
      setResult((await onLookup()) ?? null);
    } finally {
      setLooking(false);
    }
  };

  const useSuggestion = async (suggestedTeamId: string) => {
    setApplying(true);
    try {
      await onUseSuggestion(suggestedTeamId);
    } finally {
      setApplying(false);
    }
  };

  const suggestion = result?.suggestion ?? null;
  const explanation =
    result && !suggestion ? explain(result.reason, domainOf(requesterEmail)) : null;

  return (
    <Stack gap="xs" data-testid="team-lookup">
      <Group gap="sm" wrap="nowrap">
        <Text size="sm">
          Raised by <b>{requesterEmail}</b>
        </Text>
        <Button
          size="compact-sm"
          variant="light"
          onClick={() => void lookUp()}
          loading={looking}
          data-testid="team-lookup-run"
        >
          Look up team from requester
        </Button>
      </Group>

      {suggestion && (
        <Alert color="blue" variant="light" p="xs" data-testid="team-suggestion">
          <Group justify="space-between" wrap="nowrap" gap="sm">
            <Text size="sm">
              Suggested: <b>{teamName(suggestion.teamId)}</b> — the only team with users on @
              {suggestion.domain} ({suggestion.userCount}{' '}
              {suggestion.userCount === 1 ? 'user' : 'users'}).
            </Text>
            <Button
              size="compact-sm"
              onClick={() => void useSuggestion(suggestion.teamId)}
              loading={applying}
              data-testid="team-suggestion-use"
            >
              Use {teamName(suggestion.teamId)}
            </Button>
          </Group>
        </Alert>
      )}

      {explanation && (
        <Text size="sm" c="dimmed" data-testid="team-lookup-explanation">
          {explanation} Choose the team below.
        </Text>
      )}
    </Stack>
  );
}
